/**
 * Broadcast cross-app events to any native shell wrapping the portal in a
 * WebView. Used so the Angel Squad Inspirations native app (and any future
 * embedder) can silently sync the chosen Angel back to its own backend
 * without us having to know which WebView library they use.
 *
 * We fire on three transports and let whichever one is wired up consume it:
 *
 *   1) React Native WebView  → window.ReactNativeWebView.postMessage(str)
 *      - Listener in RN: `<WebView onMessage={...} />`
 *
 *   2) Native iOS WKWebView  → window.webkit.messageHandlers.angelSelect.postMessage(obj)
 *      - Handler must be registered in Swift via `userContentController.add(...)`
 *
 *   3) Generic browser / iframe → window.parent.postMessage(obj, "*")
 *      - For desktop or any host page listening on `message`.
 *
 * All three are best-effort; if a transport isn't present we silently skip it.
 *
 * @param {object} payload - Plain JSON-serialisable object.
 */
import { api } from "./api";

// Event types worth mirroring to the backend so the curator can confirm
// bridge activity from the admin health page.
const MIRRORED_TYPES = new Set([
    "bridge_ready", "angel_selected", "selection_cleared", "download_url", "new_design",
]);

export function notifyNativeShell(payload) {
    if (!payload || typeof payload !== "object") return;
    const json = JSON.stringify(payload);

    // 1) React Native WebView (most common for cross-platform shells)
    try {
        if (window.ReactNativeWebView && typeof window.ReactNativeWebView.postMessage === "function") {
            window.ReactNativeWebView.postMessage(json);
        }
    } catch {
        // ignore — listener may not exist
    }

    // 2) Native iOS WKWebView with a registered message handler named "angelSelect"
    try {
        const wk = window.webkit && window.webkit.messageHandlers;
        if (wk && wk.angelSelect && typeof wk.angelSelect.postMessage === "function") {
            wk.angelSelect.postMessage(payload);
        }
    } catch {
        // ignore
    }

    // 3) Generic window/iframe consumers
    try {
        if (window.parent && window.parent !== window) {
            window.parent.postMessage(payload, "*");
        }
        // Some shells listen on the WebView's own window instead of parent.
        window.postMessage(payload, "*");
    } catch {
        // ignore
    }

    // 4) Mirror to the backend event log (fire-and-forget, best-effort)
    try {
        if (MIRRORED_TYPES.has(payload.type)) {
            api.post("/bridge/events", { type: payload.type, payload }).catch(() => {});
        }
    } catch {
        // ignore
    }
}
